import { CarContactSheet } from './CarContactSheet';
import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { OrganizationMemberSheet } from './OrganizationMemberSheet';
import { RemoveOrganizationCarDialog } from './RemoveOrganizationCarDialog';
import { RemoveOrganizationMemberDialog } from './RemoveOrganizationMemberDialog';

type Props = {
  userGroupId: number;
  currentUserId?: number;
  translations: NotificationGroupOverviewTranslations;
  showMutationToasts: boolean;
  canManageMembers: boolean;
  canAssignMembershipRole: boolean;
  canEditMemberAccount: (member: MemberGroup) => boolean;
  canRemoveMember: (member: MemberGroup) => boolean;
  getMemberDetailUrl?: (member: Pick<MemberGroup, 'userId'>) => string;
  onSelfRemoved?: () => void;
  adminCount: number;
  memberToEdit?: MemberGroup;
  carToEdit?: CarGroup;
  memberToRemove?: MemberGroup;
  memberRemoveOpen: boolean;
  carToRemove?: CarGroup;
  carRemoveOpen: boolean;
  onCloseMember: () => void;
  onCloseCar: () => void;
  onCloseRemoveMember: () => void;
  onCloseRemoveCar: () => void;
  onRemoveMember: (member: MemberGroup) => void;
};

/** Sheets and confirmations opened from the members and cars tables. */
export const OrganizationContactOverlays = ({
  userGroupId,
  currentUserId,
  translations: t,
  showMutationToasts,
  canManageMembers,
  canAssignMembershipRole,
  canEditMemberAccount,
  canRemoveMember,
  getMemberDetailUrl,
  onSelfRemoved,
  adminCount,
  memberToEdit,
  carToEdit,
  memberToRemove,
  memberRemoveOpen,
  carToRemove,
  carRemoveOpen,
  onCloseMember,
  onCloseCar,
  onCloseRemoveMember,
  onCloseRemoveCar,
  onRemoveMember
}: Props) => (
  <>
    {memberToEdit && (
      <OrganizationMemberSheet
        userGroupId={userGroupId}
        member={memberToEdit}
        canEditAccount={canEditMemberAccount(memberToEdit)}
        canEditNotifications={canManageMembers || memberToEdit.userId === currentUserId}
        canManageAdmin={canManageMembers}
        canManagePrimaryContact={canManageMembers}
        isOnlyAdmin={memberToEdit.isAdmin && adminCount <= 1}
        canAssignDrivers={canManageMembers}
        canAssignMembershipRole={canAssignMembershipRole}
        translations={t}
        showMutationToasts={showMutationToasts}
        memberDetailUrl={getMemberDetailUrl?.(memberToEdit)}
        isSelf={memberToEdit.userId === currentUserId}
        onRemove={canRemoveMember(memberToEdit) ? () => onRemoveMember(memberToEdit) : undefined}
        onClose={onCloseMember}
      />
    )}
    {carToEdit && (
      <CarContactSheet
        open
        onOpenChange={(next) => !next && onCloseCar()}
        userGroupId={userGroupId}
        car={carToEdit}
        canEditDriver={canManageMembers}
        getMemberDetailUrl={getMemberDetailUrl}
        translations={t}
      />
    )}
    {memberToRemove && (
      <RemoveOrganizationMemberDialog
        userGroupId={userGroupId}
        member={memberToRemove}
        isOnlyAdmin={memberToRemove.isAdmin && adminCount <= 1}
        currentUserId={currentUserId}
        translations={t}
        showMutationToasts={showMutationToasts}
        chooseReplacement
        open={memberRemoveOpen}
        onSelfRemoved={onSelfRemoved}
        onClose={onCloseRemoveMember}
      />
    )}
    {carToRemove && (
      <RemoveOrganizationCarDialog
        userGroupId={userGroupId}
        car={carToRemove}
        translations={t}
        cancelText={t.cancel}
        showMutationToasts={showMutationToasts}
        open={carRemoveOpen}
        onClose={onCloseRemoveCar}
      />
    )}
  </>
);
